import { useInView } from "@/hooks/use-in-view"
import { DashboardMockup } from "./mockups/dashboard-mockup"
import { CalendarMockup } from "./mockups/calendar-mockup"
import { ProjectionMockup } from "./mockups/projection-mockup"

interface PanelProps {
	title: string
	description: string
	mockup: React.ReactNode
	reverse: boolean
}

function ShowcasePanel({ title, description, mockup, reverse }: PanelProps) {
	const { ref, inView } = useInView()

	return (
		<div ref={ref} className={`transition-opacity duration-700 ease-out ${inView ? "opacity-100" : "opacity-0"}`}>
			<div
				className={`flex flex-col ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-10 lg:gap-16`}
			>
				<div className="w-full max-w-sm sm:max-w-md lg:flex-1">{mockup}</div>
				<div className="flex-1 text-center lg:text-left">
					<h3 className="text-2xl sm:text-3xl font-bold text-sky-950 dark:text-sky-50 leading-snug">
						{title}
					</h3>
					<p className="mt-4 text-base sm:text-lg text-sky-900/70 dark:text-sky-200/60 leading-relaxed">
						{description}
					</p>
				</div>
			</div>
		</div>
	)
}

export function ProductShowcase() {
	return (
		<section className="py-20 px-4">
			<div className="max-w-5xl mx-auto space-y-20 sm:space-y-28">
				<ShowcasePanel
					title="See Your Portfolio at a Glance"
					description="Net worth, yield, annual income, allocation charts, and sortable positions — all updated in real time."
					mockup={<DashboardMockup />}
					reverse={false}
				/>
				<ShowcasePanel
					title="Know Exactly When You Get Paid"
					description="Monthly calendar with payment dates, ex-dates, and earnings reports. Export to CSV or subscribe via ICS."
					mockup={<CalendarMockup />}
					reverse={true}
				/>
				<ShowcasePanel
					title="Chart Your Path to Financial Independence"
					description="50-year DRIP projection with income targets. See exactly when dividends cover your expenses."
					mockup={<ProjectionMockup />}
					reverse={false}
				/>
			</div>
		</section>
	)
}
